import React from "react";

import { MyST } from "myst-to-react";

export const RendererContext = React.createContext(null);

const UNSUPPORTED = "unsupported";
const NOT_RANKABLE = "not-rankable";

function imageWithExt(ext) {
  return (node) => {
    return node.type === "image" && node.url.match(new RegExp(`\.${ext}$`));
  };
}
function nodeOfType(type, attrs = {}) {
  return (node) =>
    node.type === type &&
    Object.entries(attrs).every(([attr, value]) => node[attr] === value);
}

const MEDIA_LIKE = [
  nodeOfType("image"),
  nodeOfType("anywidget"),
  nodeOfType("raw"),
  nodeOfType("html"),
];

const MEDIA_LIKE_RANKING = {
  pdf: [
    nodeOfType("raw", { lang: "typst" }),
    imageWithExt("pdf"),
    imageWithExt("png"),
    imageWithExt("jpe?g"),
  ],
  web: [
    nodeOfType("anywidget"),
    nodeOfType("html"),
    imageWithExt("gif"),
    imageWithExt("svg"),
    imageWithExt("webp"),
    imageWithExt("png"),
    imageWithExt("jpe?g"),
  ],
};

/**
 * Process a single alternatives node as follows
 * 1. For each child, score the AST subtree with one of `NOT_RANKABLE` | `UNSUPPORTED` | integer;
 * 2. Prune `UNSUPPORTED` branches
 * 3. Take the branch with the lowest integer score
 *
 * NOT_RANKABLE is effectively assigned to any subtree that contains no media. Therefore
 * `alternatives` will only render non-media subtrees (e.g. paragraph of text) when there are no other alternatives containing media.
 */
function processAlternatives(node, renderer) {
  // Handle alternatives explicitly
  if (node.type === "alternatives") {
    let bestScore = UNSUPPORTED;
    let bestChild = undefined;

    node.children.forEach((c) => {
      const [index, nextChild] = processAlternatives(c, renderer);
      // Do not retain subtrees containing any unsupported branches
      if (index === UNSUPPORTED) {
        return;
      }
      // Keep this output if it is best
      if (
        bestScore === UNSUPPORTED ||
        (index !== NOT_RANKABLE && index < bestScore)
      ) {
        bestScore = index;
        bestChild = nextChild;
      }
    });

    const newChild = {
      ...node,
      children: [bestChild],
    };
    return [bestScore, newChild];
  }
  // Handle generic parent
  else if (node.children !== undefined) {
    // Recurse into parent-like node
    const newChildren = [];
    let bestScore = NOT_RANKABLE;

    for (const child of node.children) {
      const [childScore, nextChild] = processAlternatives(child, renderer);

      // If child is unsupported, early-return and propagate
      if (childScore === UNSUPPORTED) {
        return [UNSUPPORTED, node];
      }

      if (
        bestScore === NOT_RANKABLE ||
        (childScore !== NOT_RANKABLE && childScore < bestScore)
      ) {
        bestScore = childScore;
      }
      newChildren.push(nextChild);
    }

    const newChild = {
      ...node,
      children: newChildren,
    };
    return [bestScore, newChild];
  }
  // Handle leaf value
  // FIXME: do not assume that only leaves are media-like
  else {
    // Otherwise, media-like things receive a score
    const isMediaLike = MEDIA_LIKE.some((e) => e(node));

    if (isMediaLike) {
      // Provide score for media-like object
      const ranking = MEDIA_LIKE_RANKING[renderer];
      const score = ranking.findIndex((m) => m(node));
      if (score === -1) {
        return [UNSUPPORTED, node];
      } else {
        return [score, node];
      }
    }
    // Directly return value-like node
    else {
      return [NOT_RANKABLE, node];
    }
  }
}

function AlternativesNodeRenderer(props) {
  const renderer = React.useContext(RendererContext);

  const [_, node] = React.useMemo(
    () => processAlternatives(props.node, renderer),
    [props.node, renderer],
  );

  return <MyST ast={node.children} />;
}

export const ALTERNATIVES_RENDERERS = {
  alternatives: AlternativesNodeRenderer,
};
